import { FC, CSSProperties } from 'react';
import * as Headless from '@headlessui/react';

export enum Sizes {
  xs = 'xs',
  sm = 'sm',
  base = 'base',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
  '2xl' = '2xl',
  '3xl' = '3xl',
  '4xl' = '4xl',
  '5xl' = '5xl'
}

interface CheckboxButtonChoiceProps {
  text: string;
  checked: boolean;
  handleOnChange: (checked: boolean) => void;
  size?: Sizes;
  indeterminate?: boolean;
  last?: boolean;
  isSide?: boolean;
  buttonSize?: string;
  style?: CSSProperties;
  disabled?: boolean;
}

const CheckboxButtonChoice: FC<CheckboxButtonChoiceProps> = ({
  text,
  handleOnChange,
  checked = false,
  indeterminate = false,
  size = Sizes.base,
  buttonSize = "w-full",
  style,
  disabled = false
}) => {
  const baseClasses = "flex items-center cursor-pointer rounded-md transition-all duration-200";
  
  // Définition des classes de taille basées sur l'enum
  const getSizeClasses = (size: Sizes): string => {
    switch (size) {
      case Sizes.sm:
        return "px-3 py-1.5";
      case Sizes.md:
        return "px-4 py-2";
      case Sizes.lg:
        return "px-6 py-4";
      default:
        return "px-4 py-2"; // taille par défaut (base)
    }
  };

  const sizeClasses = getSizeClasses(size);
  const colorClasses = checked
    ? "bg-gray-50 text-primary border border-primary"
    : "bg-white text-gray-800 border border-gray-300 hover:border-primary";
  const disabledClasses = disabled
    ? "opacity-50 cursor-not-allowed"
    : "";

  const combinedClasses = `${baseClasses} ${sizeClasses} ${colorClasses} ${disabledClasses}`;

  const handleDivClick = () => {
    if (!disabled) {
      handleOnChange(!checked);
    }
  };

  return (
    <div
      className={`group ${combinedClasses}`}
      style={style}
      onClick={handleDivClick}
    >
      <Headless.Checkbox
        checked={checked}
        onChange={handleOnChange}
        disabled={disabled}
        className="flex items-center mr-4 focus:outline-none"
      >
        {({ checked: isChecked, disabled: isDisabled }) => (
          <span className={`
            relative inline-flex items-center justify-center w-5 h-5 
            border rounded cursor-pointer transition-all duration-200
            ${isChecked || indeterminate
              ? 'bg-primary border-primary'
              : 'bg-white border-gray-300 hover:border-gray-400'
            }
            ${isDisabled
              ? 'opacity-50 border-gray-300 bg-gray-100 pointer-events-none'
              : ''
            }
            focus:outline-2 focus:outline-primary focus:outline-offset-2
          `}>
            {/* Checkmark icon */}
            {isChecked && !indeterminate && (
              <svg
                viewBox="0 0 14 14"
                fill="none"
                className="absolute w-4 h-4 stroke-white stroke-2 opacity-100 transition-opacity duration-200"
              >
                <path
                  d="M3 8L6 11L11 3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {/* Indeterminate icon */}
            {indeterminate && (
              <svg
                viewBox="0 0 14 14"
                fill="none"
                className="absolute w-4 h-4 stroke-white stroke-2 opacity-100 transition-opacity duration-200"
              >
                <path
                  d="M3 7H11"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
        )}
      </Headless.Checkbox>
      <p className={`text-sm leading-5 ${checked ? 'font-bold' : 'font-normal'} ${buttonSize}`}>
        {text}
      </p>
    </div>
  );
};

export default CheckboxButtonChoice;
